import React, { useState, useEffect, useMemo } from "react";
import { getNotifications } from "../services/notificationService";
import { formatDate } from "../helpers/stringHelpers";
import Pagination from "../components/Pagination";
import { SearchIcon } from "../assets/icons";
import { PlusIcon } from "../assets/icons";
import YeniDuyuruModal from "../components/YeniDuyuruModal";

const ITEMS_PER_PAGE = 15;

export default function Duyurular() {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortField, setSortField] = useState("tarih");
  const [sortOrder, setSortOrder] = useState("desc"); // newest first by default
  const [isModalOpen, setIsModalOpen] = useState(false);

  const fetchNotifications = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await getNotifications();
      if (response.status === 200 && response.data) {
        setNotifications(response.data);
      } else {
        setNotifications([]);
      }
    } catch (err) {
      setError(err.message || "Duyurular yüklenirken bir hata oluştu");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchNotifications();
  }, []);

  // Filter by search term
  const filteredNotifications = useMemo(() => {
    if (!searchTerm.trim()) return notifications;
    const term = searchTerm.toLowerCase();
    return notifications.filter(
      (item) =>
        (item.mesaj && item.mesaj.toLowerCase().includes(term)) ||
        (item.gonderimDurumu && item.gonderimDurumu.toLowerCase().includes(term))
    );
  }, [notifications, searchTerm]);

  // Sort notifications
  const sortedNotifications = useMemo(() => {
    const list = [...filteredNotifications];
    const getValue = (item) => {
      if (sortField === "tarih") {
        return new Date(item.tarih).getTime() || 0;
      }
      return (item[sortField] || "").toString().toLocaleLowerCase("tr-TR");
    };
    list.sort((a, b) => {
      const va = getValue(a);
      const vb = getValue(b);
      if (va < vb) return sortOrder === "asc" ? -1 : 1;
      if (va > vb) return sortOrder === "asc" ? 1 : -1;
      return 0;
    });
    return list;
  }, [filteredNotifications, sortField, sortOrder]);

  // Paginate
  const totalPages = Math.ceil(sortedNotifications.length / ITEMS_PER_PAGE);
  const paginatedNotifications = useMemo(() => {
    const start = currentPage * ITEMS_PER_PAGE;
    return sortedNotifications.slice(start, start + ITEMS_PER_PAGE);
  }, [sortedNotifications, currentPage]);

  // Reset page when search changes
  useEffect(() => {
    setCurrentPage(0);
  }, [searchTerm]);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortOrder(field === "tarih" ? "desc" : "asc");
    }
  };

  const handleSearch = () => {
    setCurrentPage(0);
  };

  const handleSearchKeyPress = (e) => {
    if (e.key === "Enter") {
      handleSearch();
    }
  };

  const getSortIndicator = (field) => {
    if (sortField !== field) return null;
    return sortOrder === "asc" ? " ▲" : " ▼";
  };

  const handleOpenModal = () => {
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  const handleModalSuccess = () => {
    // Refresh notifications after successful submission
    fetchNotifications();
  };

  if (loading) return <div className="loading-container">Yükleniyor...</div>;
  if (error) return <div className="error-container">Hata: {error}</div>;

  return (
    <div className="duyurular-container">
      <div className="page-header">
        <h1>Bildirimler</h1>
      </div>

      <div className="search-container">
        <div className="search-inputs">
          <div className="search-input-group">
            <input
              type="text"
              placeholder="Mesaj veya durum ara..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyPress={handleSearchKeyPress}
              className="search-input"
            />
            <button onClick={handleSearch} className="search-button">
              <SearchIcon className="search-icon" />
            </button>
          </div>
          <div className="results-info">
            <span>
              Toplam {sortedNotifications.length} kayıt bulundu
            </span>
          </div>
          <button className="yeni-duyuru-button" onClick={handleOpenModal}>
            <PlusIcon color="white" />
            <span>Yeni Bildirim</span>
          </button>
        </div>
      </div>

      <div className="table-container">
        <table className="personel-table duyurular-table">
          <thead>
            <tr>
              <th
                onClick={() => handleSort("tarih")}
                style={{ cursor: "pointer", width: "150px" }}
              >
                Tarih{getSortIndicator("tarih")}
              </th>
              <th
                onClick={() => handleSort("gonderimDurumu")}
                style={{ cursor: "pointer", width: "150px" }}
              >
                Gönderim Durumu{getSortIndicator("gonderimDurumu")}
              </th>
              <th
                onClick={() => handleSort("mesaj")}
                style={{ cursor: "pointer" }}
              >
                Mesaj{getSortIndicator("mesaj")}
              </th>
            </tr>
          </thead>
          <tbody>
            {paginatedNotifications.length === 0 ? (
              <tr>
                <td colSpan="3" style={{ textAlign: "center", padding: "20px" }}>
                  Kayıt bulunamadı
                </td>
              </tr>
            ) : (
              paginatedNotifications.map((item, index) => (
                <tr key={index}>
                  <td>{formatDate(item.tarih)}</td>
                  <td>
                    <span
                      className={`status-badge ${
                        item.gonderimDurumu === "Gönderildi"
                          ? "status-sent"
                          : "status-pending"
                      }`}
                    >
                      {item.gonderimDurumu}
                    </span>
                  </td>
                  <td>
                    <div className="mesaj-text">{item.mesaj}</div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {totalPages > 1 && (
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={setCurrentPage}
        />
      )}

      <YeniDuyuruModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        onSuccess={handleModalSuccess}
      />
    </div>
  );
}
